import { http } from '@/api/http'

export type ChatKind = 'order' | 'direct' | 'group'
export type ChatRole = 'owner' | 'admin' | 'member'
export type ChatMessageKind = 'text' | 'file' | 'system'
export type ChatSystemCode = 'status_changed' | 'driver_assigned' | 'driver_gave_back' | 'order_created'

export type ChatAttachment = {
  id: string
  name: string
  size: number
  content_type: string
  /** Fresh on every read (signed when the bucket is private): never store it. */
  url: string
}

export type ChatSystemPayload = {
  status?: string
  from_status?: string
  order_number?: number
  driver_name?: string
}

export type ChatMessage = {
  id: number
  chat_id: string
  /** '' for system messages. */
  sender_profile_id: string
  sender_name: string
  sender_role_code: string
  kind: ChatMessageKind
  /** '' when deleted (messages cannot be deleted from the console; older data may carry it). */
  body: string
  reply_to_id: number
  system_code: ChatSystemCode | ''
  system_payload: ChatSystemPayload | null
  attachments: ChatAttachment[]
  mine: boolean
  edited_at: string
  deleted_at: string
  created_at: string
}

export type Chat = {
  id: string
  kind: ChatKind
  /** Group: its title; direct: the other person's name; order: '' («Заказ №{order_number}»). */
  title: string
  order_id: string
  order_number: number
  order_status: string
  peer_profile_id: string
  peer_role_code: string
  last_message: ChatMessage | null
  last_message_at: string
  unread_count: number
  muted: boolean
  member_count: number
  /** '' = not a member (an admin reading an order chat). */
  my_role: ChatRole | ''
  is_member: boolean
  /** Group owner/admin: rename, add and remove members. */
  can_manage: boolean
  created_at: string
}

export type ChatUnread = { total: number; order: number; direct: number; group: number }

export type ChatMember = {
  profile_id: string
  name: string
  role_code: string
  role: ChatRole
  source: 'auto' | 'manual'
  last_read_message_id: number
}

export type ChatContact = { profile_id: string; name: string; role_code: string }

/** A file already stored through POST /file-upload. */
export type ChatAttachmentInput = { key: string; name: string; size: number; content_type: string }

export type ChatMessagesPage = { items: ChatMessage[]; has_more: boolean }

export type SendChatMessage = {
  body: string
  attachments?: ChatAttachmentInput[]
  reply_to_id?: number
  /** Makes a resend after a network failure idempotent. */
  client_msg_id: string
}

const message = (row: ChatMessage): ChatMessage => ({ ...row, attachments: row.attachments ?? [] })
const chat = (row: Chat): Chat => ({ ...row, last_message: row.last_message ? message(row.last_message) : null })

/**
 * Internal chat (/v1/chats): order chats, direct and group chats of the
 * signed-in profile. Every call needs chat.use; the server scopes everything
 * to the caller and the tenant of the token.
 */
export const chatService = {
  /** `scope: 'all'` (chat.read_all only): every order chat with messages and every group, `is_member` marks mine. */
  async list(params: { kind?: ChatKind; search?: string; scope?: 'all'; limit?: number; offset?: number } = {}, signal?: AbortSignal) {
    const { data } = await http.get<{ count: number; items: Chat[] | null }>('/chats', { params, signal })
    return { count: data.count ?? 0, items: (data.items ?? []).map(chat) }
  },
  async unreadCount(signal?: AbortSignal) {
    const { data } = await http.get<ChatUnread>('/chats/unread-count', { signal })
    return data
  },
  async contacts(search = '', signal?: AbortSignal) {
    const { data } = await http.get<ChatContact[] | null>('/chats/contacts', { params: search ? { search } : undefined, signal })
    return data ?? []
  },
  /** Get-or-create the direct chat with that person. */
  async direct(profileId: string) {
    const { data } = await http.post<Chat>('/chats/direct', { profile_id: profileId })
    return chat(data)
  },
  /** Needs chat.groups.create. */
  async createGroup(body: { title: string; profile_ids: string[] }) {
    const { data } = await http.post<Chat>('/chats/groups', body)
    return chat(data)
  },
  /** Get-or-create the order's chat; 403/404 when the order is not visible to the caller. */
  async byOrder(orderId: string, signal?: AbortSignal) {
    const { data } = await http.get<Chat>(`/chats/by-order/${orderId}`, { signal })
    return chat(data)
  },
  async get(id: string, signal?: AbortSignal) {
    const { data } = await http.get<Chat>(`/chats/${id}`, { signal })
    return chat(data)
  },
  async rename(id: string, title: string) {
    const { data } = await http.patch<Chat>(`/chats/${id}`, { title })
    return chat(data)
  },
  async members(id: string, signal?: AbortSignal) {
    const { data } = await http.get<ChatMember[] | null>(`/chats/${id}/members`, { signal })
    return data ?? []
  },
  async addMembers(id: string, profileIds: string[]) {
    const { data } = await http.post<ChatMember[] | null>(`/chats/${id}/members`, { profile_ids: profileIds })
    return data ?? []
  },
  async removeMember(id: string, profileId: string) {
    await http.delete(`/chats/${id}/members/${profileId}`)
  },
  async leave(id: string) {
    await http.post(`/chats/${id}/leave`)
  },
  async mute(id: string, muted: boolean) {
    await http.post(`/chats/${id}/mute`, { muted })
  },
  /** Ascending by id. `before_id` pages back, `after_id` polls for new ones. */
  async messages(id: string, params: { before_id?: number; after_id?: number; limit?: number } = {}, signal?: AbortSignal) {
    const { data } = await http.get<{ items: ChatMessage[] | null; has_more: boolean }>(`/chats/${id}/messages`, { params, signal })
    return { items: (data.items ?? []).map(message), has_more: Boolean(data.has_more) } satisfies ChatMessagesPage
  },
  /** Sending also marks the chat read for the sender. */
  async send(id: string, body: SendChatMessage) {
    const { data } = await http.post<ChatMessage>(`/chats/${id}/messages`, body)
    return message(data)
  },
  async edit(id: string, messageId: number, body: string) {
    const { data } = await http.patch<ChatMessage>(`/chats/${id}/messages/${messageId}`, { body })
    return message(data)
  },
  /** Moves the caller's read mark forward only. */
  async read(id: string, messageId: number) {
    await http.post(`/chats/${id}/read`, { message_id: messageId })
  },
}
